import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Form, Formik } from 'formik'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import * as yup from 'yup'
import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'

import api from 'api'
import { AuthStateContext } from 'context/auth'
import { makeSubscribable } from 'helpers'
import { passwordHelperText } from 'constants/index'

import FormikTextField from 'components/Common/FormikTextField'
import ZendeskLink from 'components/Common/ZendeskLink'
import Button from 'components/Common/Button'
import { PRIMARY_BUTTON, DISABLED_BUTTON } from 'themes/theme'
import { checkCookie, setCookie } from 'utils/helper'
import Link from '@mui/material/Link';

const errorVerificationCodeMessage = 'Invalid verification code, please check your email and try again.'

export const passwordFieldSchema = yup
  .string()
  .min(8, 'Password must be at least 8 characters')
  .required('Password is required')
  .matches(/[a-z]+/, 'Password requires lowercase letters')
  .matches(/[A-Z]+/, 'Password requires uppercase letters')
  .matches(/[0-9]+/, 'Password requires numbers')
  .matches(/[!@#$%^&*)(+=.<>{}/[\\\]:;'"|~`_-]+/, 'Password requires special characters')

export const passwordConfirmFieldSchema = yup
  .string()
  .required('Password confirm is required')
  .test('passwords-match', 'Passwords must match', (value, context) => {
    return context.parent.password === value
  })

const schema = yup.object({
  code: yup.string().min(6, 'Code must be at least 6 characters').required('Code is required'),
  password: passwordFieldSchema,
  passwordConfirm: passwordConfirmFieldSchema,
})

const clickHereSx = {
  color: "secondary.main",
  fontWeight: "bold",
  textDecoration: "none",
  fontSize: "inherit"
}

const sx = {
  root: {
    maxWidth: (theme) => theme.spacing(47),
  },
  header: { marginBottom: (theme) => theme.spacing(3) },
  fieldWrapper: {
    minHeight: 85,
    marginBottom: (theme) => theme.spacing(3),
  },
  textWrapper: {
    marginBottom: (theme) => theme.spacing(2),
  },
  button: {
    marginTop: (theme) => theme.spacing(3),
    marginBottom: (theme) => theme.spacing(2),
  },
  titleWrapper: {
    minHeight: (theme) => theme.spacing(15),
    color: 'var(--primary)',
  },
  inputWrapper: {
    position: 'relative',
    height: 104,
  },
  submitButton: {
    display: 'block',
    margin: '0 auto 20px',
  },
  generalError: {
    color: 'red',
    marginTop: (theme) => theme.spacing(2),
  },
  link: {
    color: '#0076C5',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};

const PasswordText = styled(Box)(() => ({
  fontWeight: 'lighter',
  fontFamily: 'poppins',
  fontSize: '12px',
}))

const initialValue = {
  code: '',
  password: '',
  passwordConfirm: '',
}

export const CreateNewPasswordForm = () => {
  const [{ email, password }, authDispatch] = useContext(AuthStateContext)
  const formik = useRef(null)
  const [createNewPasswordSubscribable, setCreateNewPasswordSubscribable] = useState(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [sending, setSending] = useState(false)

  const onFormSubmit = useCallback(
    values => {
      const promise = api.forgotPasswordSubmit(email.toLowerCase(), values.code, values.password)
      const subscribable = makeSubscribable(promise)
      authDispatch({ type: 'createNewPasswordFormSubmitted', payload: { email, ...values } })
      setCreateNewPasswordSubscribable(subscribable)
    },
    [email, authDispatch],
  )

  const onPromiseSuccess = useCallback(async () => {
    if (email && password) {
      try {
        await api.signIn(email.toLowerCase(), password)
        const user = await api.getUser()
        dispatch({ type: 'userLoaded', user })
        if (checkCookie(user.email, user.id)) {
          navigate('/');
        } else {
          setCookie(user.email, user.id, 365)
          navigate('/');
        }
      } catch (error) {
        console.log(error)
      }
    }
  }, [email, password, dispatch, navigate])

  const onPromiseError = useCallback(error => {
    switch (error.name) {
      case 'CodeMismatchException':
        formik.current?.setErrors({ code: errorVerificationCodeMessage })
        break

      default: {
        formik.current?.setErrors({ code: error.message })
      }
    }
    formik.current?.setSubmitting(false)
  }, [])

  async function handleClickHere(e) {
    setSending(true);
    e.preventDefault();
    await api.forgotPassword(email.toLowerCase());
    setSending(false);
  }

  useEffect(() => {
    if (createNewPasswordSubscribable) {
      return createNewPasswordSubscribable.subscribe(onPromiseSuccess, onPromiseError)
    }
  }, [onPromiseError, onPromiseSuccess, createNewPasswordSubscribable])

  return (
    <>
      <Formik innerRef={formik} validationSchema={schema} initialValues={initialValue} onSubmit={onFormSubmit}>
        {({ isSubmitting, isValid }) => (
          <Form>
            <Box sx={sx.titleWrapper}>
              <Typography variant="h5" style={{ fontSize: '29px' }} sx={sx.header} className={`mb-5`}>
                Create your new password
              </Typography>
              <Typography component="span" variant="body2" sx={sx.textWrapper} className={`d-inline-block fw-light mb-4`}>
                <PasswordText>{passwordHelperText}</PasswordText>
              </Typography>
            </Box>
            <Box sx={sx.fieldWrapper}>
              <FormikTextField name="code" label="Code" fullWidth autoFocus type="password" />
            </Box>
            <Box sx={sx.fieldWrapper}>
              <FormikTextField name="password" label="New Password" fullWidth type="password" />
            </Box>
            <Box sx={sx.fieldWrapper} style={{ marginBottom: 0 }}>
              <FormikTextField name="passwordConfirm" label="Confirm New Password" fullWidth type="password" />
            </Box>

            <Typography sx={{ fontSize: 10, lineHeight: "14px" }}>
              Didn't receive your code? Please refresh your email inbox, check spam and junk folders,
              or
              {sending
                ? <Typography component="span" sx={clickHereSx}> Sending...</Typography>
                : <Link href="#" onClick={handleClickHere} sx={clickHereSx}> Click Here</Link>
              } to send a new confirmation code
            </Typography>

            <Box textAlign='center'>
              <Button
                type={!isValid ? DISABLED_BUTTON : PRIMARY_BUTTON}
                style={{ width: '250px', margin: '20px' }}
                disabled={isSubmitting || !isValid}
                submit
                loading={isSubmitting}
              >
                Submit
              </Button>
            </Box>
            <ZendeskLink />
          </Form>
        )}
      </Formik>
    </>
  )
}
